import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
// @ts-expect-error: Incorrect type
import DraggableObjectTarget from 'ember-drag-drop/components/draggable-object-target';
import { t } from 'ember-intl';
import SettingsRecommendationsCompetitorsImpactDragHandle from 'lumen/components/settings/recommendations/competitors-impact/drag-handle';
import type { FormattedCompetitorsType } from 'lumen/components/settings/recommendations/competitors-impact-picker';
import type { RecommendationStrategy } from 'lumen/models';
import type { CompsetPositioningLevelValues } from 'lumen/models/recommendation-strategy';
import type { COMPETITORS_IMPACTS_STRATEGY_LEVELS } from 'lumen/utils/constants/pricing-assistant-constants';
import {
  COMPETITORS_IMPACTS_STRATEGY_LEVELS_ICON_CLASSES,
  COMPETITORS_IMPACTS_STRATEGY_LEVELS_TRANSLATION_KEY_MAPPING,
} from 'lumen/utils/constants/pricing-assistant-constants';

interface SettingsRecommendationsCompetitorsImpactDataTableRowSignature {
  Args: {
    competitorLevel: (typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS)[keyof typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS];
    formattedCompetitors: Record<string, FormattedCompetitorsType>;
    formattedCompetitorsCompetitorsByLevel: Record<
      CompsetPositioningLevelValues,
      FormattedCompetitorsType[]
    >;
    isEditMode: boolean;
    onCompetitorImpactUpdate: (
      competitorId: string,
      newLevel: CompsetPositioningLevelValues,
    ) => void;
    strategy: RecommendationStrategy;
  };
}

export default class SettingsRecommendationsCompetitorsImpactDataTableRow extends Component<SettingsRecommendationsCompetitorsImpactDataTableRowSignature> {
  @service declare intl: Services['intl'];

  @tracked isDraggableObjectOverDroppableArea = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsInThisLevel() {
    if (
      !this.args.formattedCompetitorsCompetitorsByLevel[
        this.args.competitorLevel
      ]
    ) {
      return [];
    }
    return this.args.formattedCompetitorsCompetitorsByLevel[
      this.args.competitorLevel
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconClass() {
    return COMPETITORS_IMPACTS_STRATEGY_LEVELS_ICON_CLASSES[
      this.args.competitorLevel
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    return this.intl.t(
      COMPETITORS_IMPACTS_STRATEGY_LEVELS_TRANSLATION_KEY_MAPPING[
        this.args.competitorLevel
      ],
    );
  }

  @action onDragOut(): void {
    this.isDraggableObjectOverDroppableArea = false;
  }

  @action onDragOver(): void {
    this.isDraggableObjectOverDroppableArea = true;
  }

  @action onDrop(draggedObject: unknown): void {
    this.args.onCompetitorImpactUpdate(
      (draggedObject as { id: string; title: string }).id,
      this.args.competitorLevel,
    );
  }

  <template>
    <div class="data-table__row flex flex-row">
      <div class="flex gap-100 pt-300 pr-200 pb-300 pl-400">
        <div
          class="data-table__icon mt-100 mr-50 mb-100 ml-50 {{this.iconClass}}"
        />
        <div class="data-table__title flex flex-col">
          <p class="text-400--regular">{{this.title}}</p>
          <p class="text-200--regular data-table__subtitle">{{t
              "general.vs_my_hotel"
            }}</p>
        </div>
      </div>

      {{#if @isEditMode}}
        <DraggableObjectTarget
          @action={{this.onDrop}}
          @dragOutAction={{this.onDragOut}}
          @dragOverAction={{this.onDragOver}}
        >
          <div class="flex w-full items-center pt-300 pr-400 pb-300 pl-400">
            <div
              class="data-table__draggable-area data-table__draggable-area--droppable flex h-full w-full flex-wrap gap-400
                {{unless
                  this.competitorsInThisLevel.length
                  'data-table__draggable-area--empty items-center justify-center'
                }}
                {{if
                  this.isDraggableObjectOverDroppableArea
                  'data-table__draggable-area--dragged-over'
                }}"
            >
              {{#if this.competitorsInThisLevel.length}}
                {{#each this.competitorsInThisLevel as |competitor|}}
                  <SettingsRecommendationsCompetitorsImpactDragHandle
                    @id={{competitor.id}}
                    @isEditMode={{@isEditMode}}
                    @title={{competitor.name}}
                  />
                {{/each}}
              {{else}}
                <p class="text-200--regular placeholder-text">{{t
                    "pricing_assistant.recommendations.competitors_impacts.drag_and_drop_placeholder"
                  }}</p>
              {{/if}}
            </div>
          </div>
        </DraggableObjectTarget>
      {{else}}
        <div class="flex w-full items-center pt-300 pr-400 pb-300 pl-400">
          <div
            class="data-table__draggable-area flex h-full w-full flex-wrap gap-400
              {{unless
                this.competitorsInThisLevel.length
                'data-table__draggable-area--empty items-center justify-center'
              }}"
          >
            {{#if this.competitorsInThisLevel.length}}
              {{#each this.competitorsInThisLevel as |competitor|}}
                <SettingsRecommendationsCompetitorsImpactDragHandle
                  @id={{competitor.id}}
                  @isEditMode={{@isEditMode}}
                  @title={{competitor.name}}
                />
              {{/each}}
            {{else}}
              <p class="text-200--regular placeholder-text">{{t
                  "pricing_assistant.recommendations.competitors_impacts.level_category.empty"
                }}</p>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
